import React, { useEffect, useRef, useState, useMemo } from "react";
import tottiesLogo from "../img/totties.svg";
import tottiesLogoText from "../img/totties_text.svg";
import { useAuth } from "./AuthProvider";
import { isApiConfigured } from "../utils/api";
import CardNav, { type CardNavItem } from "../react-bits/CardNav/CardNav";
import { ROUTES } from "../routes";
import "./Header.css";

type AuthView = "signin" | "signup";

interface HeaderProps {
  onNavigate: (path: string) => void;
  pathname: string;
  onOpenAuth: (view?: AuthView) => void;
  onOpenSavedModal?: () => void;
  onRestoreCalculation?: (
    type: import("../utils/api").CalculationType,
    payload: Record<string, unknown>
  ) => void;
}

const Header: React.FC<HeaderProps> = ({
  onNavigate,
  pathname,
  onOpenAuth,
  onOpenSavedModal,
}) => {
  const { user, loading, signOut } = useAuth();
  const [theme, setTheme] = useState<string>(
    typeof window !== "undefined"
      ? (localStorage.getItem("theme") as string) || "system"
      : "system"
  );
  const [avatarOpen, setAvatarOpen] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target as Node)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    const prefersDark =
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = theme === "dark" || (theme === "system" && prefersDark);
    root.setAttribute("data-theme", isDark ? "dark" : "light");
  }, [theme]);

  const goTo = (path: string) => () => onNavigate(path);

  const navItems: CardNavItem[] = useMemo(() => {
    const getStartedLinks = user
      ? [
          ...(isApiConfigured() && onOpenSavedModal
            ? [
                {
                  label: "Saved calculations",
                  onClick: () => onOpenSavedModal(),
                  ariaLabel: "Open saved calculations",
                },
              ]
            : []),
          {
            label: "Sign out",
            onClick: () => signOut(),
            ariaLabel: "Sign out",
          },
        ]
      : [
          {
            label: "Login",
            onClick: () => onOpenAuth("signin"),
            ariaLabel: "Log in",
          },
          {
            label: "Registration",
            onClick: () => onOpenAuth("signup"),
            ariaLabel: "Create an account",
          },
        ];

    return [
      {
        label: "Calculators",
        bgColor: "#1e3a8a",
        textColor: "#f8fafc",
        links: [
          {
            label: "Car Affordability",
            onClick: goTo(ROUTES.carAffordability),
            ariaLabel: "Car affordability calculator",
          },
          {
            label: "Home Affordability",
            onClick: goTo(ROUTES.homeAffordability),
            ariaLabel: "Home affordability calculator",
          },
          {
            label: "Bond Repayment",
            onClick: goTo(ROUTES.bondRepayment),
            ariaLabel: "Bond repayment calculator",
          },
        ],
      },
      {
        label: "Tools",
        bgColor: "#0f766e",
        textColor: "#ecfdf5",
        links: [
          {
            label: "Monthly Finances",
            onClick: goTo(ROUTES.monthlyFinances),
            ariaLabel: "Monthly finances",
          },
          {
            label: "Tax",
            onClick: goTo(ROUTES.tax),
            ariaLabel: "Tax calculator",
          },
        ],
      },
      {
        label: "Get started",
        bgColor: "#4338ca",
        textColor: "#eef2ff",
        links: getStartedLinks,
      },
    ];
  }, [user, onOpenAuth, onOpenSavedModal, onNavigate, signOut]);

  const cycleTheme = () => {
    const next =
      theme === "light" ? "dark" : theme === "dark" ? "system" : "light";
    setTheme(next);
    localStorage.setItem("theme", next);
  };

  const isDarkTheme =
    theme === "dark" ||
    (theme === "system" &&
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches);

  const isLanding = pathname === ROUTES.home;

  return (
    <>
      <header
        className={`app-header${isLanding ? " app-header--landing" : ""}`}
      >
        <div
          className={`header-container header-container--cardnav${isLanding ? " header-container--cardnav-landing" : ""}`}
        >
          <CardNav
            key={isLanding ? "landing" : "app"}
            layout={isLanding ? "floating" : "header"}
            className="totties-card-nav"
            logo={isLanding ? tottiesLogo : tottiesLogoText}
            logoAlt="Totties"
            onLogoClick={() => onNavigate(ROUTES.home)}
            items={navItems}
            baseColor={
              isLanding
                ? "rgba(15, 23, 42, 0.45)"
                : isDarkTheme
                  ? "#0b1220"
                  : "#ffffff"
            }
            menuColor={isLanding ? "#f8fafc" : "var(--color-text)"}
            buttonBgColor={
              isLanding ? "rgba(37, 99, 235, 0.9)" : "var(--color-accent)"
            }
            buttonTextColor="#fff"
            ctaLabel={user ? "Saved" : "Login"}
            onCtaClick={
              user && isApiConfigured() && onOpenSavedModal
                ? onOpenSavedModal
                : () => onOpenAuth("signin")
            }
            hideCta={loading}
            topBarExtra={
              !loading ? (
                <div className="header-cardnav-extras">
                  {user && (
                    <span className="header-user header-user--compact">
                      {user.email ?? user.username}
                    </span>
                  )}
                  <div className="header-avatar-wrap" ref={avatarRef}>
                    <button
                      type="button"
                      className="header-avatar-btn"
                      onClick={() => setAvatarOpen((o) => !o)}
                      aria-label="Account menu"
                      aria-expanded={avatarOpen}
                    >
                      {user ? (
                        <span className="header-avatar-initials">
                          {(user.email ?? user.username ?? "?")[0].toUpperCase()}
                        </span>
                      ) : (
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          aria-hidden
                        >
                          <circle cx="12" cy="8" r="4" />
                          <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                        </svg>
                      )}
                    </button>
                    {avatarOpen && (
                      <div className="header-avatar-dropdown" role="menu">
                        {user ? (
                          <>
                            <span className="avatar-dropdown-email">
                              {user.email ?? user.username}
                            </span>
                            {isApiConfigured() && onOpenSavedModal && (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={() => {
                                  setAvatarOpen(false);
                                  onOpenSavedModal();
                                }}
                              >
                                Saved calculations
                              </button>
                            )}
                            <button
                              type="button"
                              role="menuitem"
                              className="avatar-dropdown-signout"
                              onClick={() => {
                                setAvatarOpen(false);
                                signOut();
                              }}
                            >
                              Sign out
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setAvatarOpen(false);
                                onOpenAuth("signin");
                              }}
                            >
                              Login
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setAvatarOpen(false);
                                onOpenAuth("signup");
                              }}
                            >
                              Registration
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : null
            }
          />
        </div>
      </header>

      <button
        type="button"
        className="theme-fab"
        onClick={cycleTheme}
        aria-label={`Theme: ${theme}`}
        title={
          theme === "dark"
            ? "Dark mode"
            : theme === "light"
              ? "Light mode"
              : "System theme"
        }
      >
        {theme === "dark" ? "🌙" : theme === "light" ? "☀️" : "🖥️"}
      </button>
    </>
  );
};

export default Header;
